import type { NavigationMenuItem } from '#ui/types'
import type { Initiative } from '~/types/initiatives'

/**
 * Composable for managing sidebar navigation items with permission-based visibility
 *
 * This composable generates sidebar menu items based on the current route and user permissions.
 * It ensures that users only see navigation items they have access to, improving UX by
 * preventing navigation to pages they cannot access.
 *
 * @returns {Object} Object containing menuItems, bottomItems, and sharedUI
 */
export function useSidebarItems(initiative?: Ref<Initiative | undefined>) {
  const route = useRoute()
  const { getNavigationUrl } = useNavigationPreservation()
  const { user } = useAuth()

  const sharedUI: NavigationMenuItem['ui'] = {
    link: 'text-md',
  }

  const menuItems = computed(() => {
    const initiativeId = route.params.id as string

    // Landing page sidebar items
    if (!initiativeId) {
      return [
        [
          {
            label: 'Initiatieven',
            icon: 'i-tabler-briefcase',
            to: getNavigationUrl('/initiatives').value,
            ui: sharedUI,
          },
        ],
        [
          // Permission-based navigation: Only show Contacts if user has list permission
          // This prevents users from seeing navigation items they cannot access
          ...(user.value?.permissions?.contacts?.list
            ? [
                {
                  label: 'Contacten',
                  icon: 'i-tabler-user',
                  to: `/contacts`,
                  ui: {
                    link: 'text-md',
                    root: 'self-end',
                  },
                },
              ]
            : []),
          // Permission-based navigation: Only show Organizations if user has list permission
          // This prevents users from seeing navigation items they cannot access
          ...(user.value?.permissions?.organizations?.list
            ? [
                {
                  label: 'Organisaties',
                  icon: 'i-tabler-buildings',
                  to: `/organizations`,
                  ui: {
                    link: 'text-md',
                    root: 'self-end',
                  },
                },
              ]
            : []),
        ].filter(Boolean),
      ]
    }

    // Initiative page sidebar items
    return [
      // Group 1
      [
        {
          label: 'Overzicht',
          icon: 'i-tabler-timeline',
          to: `/initiatives/${initiativeId}/initiative`,
          exact: true,
          ui: sharedUI,
        },
        {
          label: 'Checklist',
          icon: 'i-tabler-square-check',
          to: `/initiatives/${initiativeId}/checklist`,
          ui: sharedUI,
        },
      ],
      // Group 2
      [
        {
          label: 'Gebieden',
          icon: 'i-tabler-map-2',
          to: `/initiatives/${initiativeId}/areas`,
          ui: sharedUI,
        },
        {
          label: 'KLIC',
          icon: 'i-tabler-share',
          to: `/initiatives/${initiativeId}/klic`,
          ui: sharedUI,
        },
        {
          label: 'Overige informatie',
          icon: 'i-tabler-folder',
          to: `/initiatives/${initiativeId}/area-data`,
          ui: sharedUI,
        },
        {
          label: 'Lokaliseren',
          icon: 'i-tabler-crosshair',
          to: `/initiatives/${initiativeId}/locate`,
          ui: sharedUI,
        },
      ],
      // Group 3
      [
        {
          label: 'Ontwerp',
          icon: 'i-tabler-vector',
          to: `/initiatives/${initiativeId}/designs`,
          ui: sharedUI,
        },
      ],
      // Group 4
      [
        {
          label: "Risico's",
          icon: 'i-tabler-alert-triangle',
          to: `/initiatives/${initiativeId}/risks`,
          ui: sharedUI,
        },
        {
          label: 'Maatregelen',
          icon: 'i-tabler-list',
          to: `/initiatives/${initiativeId}/mitigations`,
          ui: sharedUI,
        },
        {
          label: 'Werkinstructies',
          icon: 'i-tabler-shovel',
          to: `/initiatives/${initiativeId}/work-instructions`,
          ui: sharedUI,
        },
      ],
      // Group 6
      [
        {
          label: 'Maatregelenplan',
          icon: 'i-tabler-file-text-shield',
          to: `/initiatives/${initiativeId}/mitigation-plan`,
          ui: sharedUI,
        },
        {
          label: 'Instructierapport',
          icon: 'i-tabler-clipboard-text',
          to: `/initiatives/${initiativeId}/instruction-report`,
          ui: sharedUI,
        },
      ],
      // Group 7
      [
        {
          label: 'Schades',
          icon: 'i-tabler-tools',
          to: `/initiatives/${initiativeId}/utility-strikes`,
          ui: sharedUI,
        },
        {
          label: 'Afwijkingen',
          icon: 'i-tabler-arrows-diff',
          to: `/initiatives/${initiativeId}/utility-deviations`,
          ui: sharedUI,
        },
      ],
      // Group 8
      [
        {
          label: 'Evaluatie',
          icon: 'i-tabler-zoom',
          to: `/initiatives/${initiativeId}/evaluation-items`,
          ui: sharedUI,
        },
      ],
      // Group 9
      [
        {
          label: 'Team',
          icon: 'i-tabler-users',
          to: `/initiatives/${initiativeId}/team`,
          ui: sharedUI,
        },
        // Permission-based navigation: Only show Organizations if user has list permission
        // This prevents users from seeing navigation items they cannot access in initiative context
        ...(user.value?.permissions?.organizations?.list
          ? [
              {
                label: 'Organisaties',
                icon: 'i-tabler-buildings',
                to: `/initiatives/${initiativeId}/organizations`,
                ui: sharedUI,
              },
            ]
          : []),
      ].filter(Boolean),
    ]
  })

  const bottomItems: NavigationMenuItem[] = []

  return {
    menuItems,
    bottomItems,
    sharedUI,
  }
}
